import type { InAppMessage, ControlMessage } from '@braze/web-sdk'
import { isDevEnvironment, normalizePageName } from '../Window/windowHelpers'
import { getBrazeSDK } from '../../Config/Braze/braze.config'
import { BRAZE_PAGE_NAMES } from '../../Constants'

export type BrazeMessage = InAppMessage | ControlMessage

/**
 * Default message handler that auto-shows in-app messages
 * @param braze Braze SDK instance
 * @param message The received message
 */
export const defaultInAppMessageHandler = async (
  message: BrazeMessage,
  lastTrackedPageName?: string
): Promise<boolean> => {
  if (isDevEnvironment()) {
    console.log('[Braze IAM] Handling message with default handler:', message)
  }
  let success = false
  const braze = await getBrazeSDK()
  if (braze) {
    // Braze IAM have a closeMessage method
    if ('closeMessage' in message) {
      if (typeof braze.showInAppMessage === 'function') {
        try {
          const normalizedLocationName = normalizePageName(message.extras?.location)
          if (lastTrackedPageName === normalizedLocationName) {
            braze.showInAppMessage(message)
            success = true
          }
        } catch (error) {
          console.error('[Braze IAM] Error showing in-app message:', error)
        }
      } else {
        console.warn('[Braze IAM] showInAppMessage method not available')
      }
    }
  } else {
    console.error('[Braze] Trouble connecting to braze:')
  }
  return success
}

/**
 * Creates a subscription to Braze in-app messages
 * @param braze Braze SDK instance
 * @param onMessage Message handler function (defaults to auto-show)
 * @returns subscription ID
 */
export const subscribeToInAppMessages = (
  braze: any,
  onMessage?: (message: BrazeMessage) => void
): string | undefined => {
  if (typeof braze.subscribeToInAppMessage !== 'function') {
    console.warn('[Braze IAM] subscribeToInAppMessage method not available')
    return undefined
  }

  if (isDevEnvironment()) {
    console.log('[Braze IAM] Creating subscription')
  }

  try {
    const subscriptionId = braze.subscribeToInAppMessage((message: BrazeMessage) => {
      if (isDevEnvironment()) {
        console.log('[Braze IAM] Received message:', message)
      }
      if (onMessage) {
        onMessage(message)
      }
    })

    if (isDevEnvironment()) {
      console.log('[Braze IAM] Subscription ID:', subscriptionId)
    }

    return subscriptionId
  } catch (subscriptionError) {
    console.error('[Braze IAM] Error creating subscription:', subscriptionError)
    return undefined
  }
}

/**
 * Requests immediate data flush from Braze (fetches pending messages)
 * @param braze Braze SDK instance
 */
export const requestInAppMessageFlush = (braze: any): void => {
  if (typeof braze?.requestImmediateDataFlush === 'function') {
    if (isDevEnvironment()) {
      console.log('[Braze IAM] Requesting immediate data flush')
    }
    braze.requestImmediateDataFlush()
  }
}

/**
 * Maps the page name to the Braze page name
 * @param value The page name
 * @returns The Braze page name
 */
export const mapBrazePageName = (value?: string) => {
  if (!value) return undefined
  if(value in BRAZE_PAGE_NAMES) {
    return BRAZE_PAGE_NAMES[value]
  }
  return undefined
}
